import { apiFetch, apiFetchBlob, ApiClientError } from "./client";

/**
 * D-143 C7 — майстер додаткової угоди: context / registry-lookup / requisites /
 * preview / generate. Всі помилки — flat-тунель {code, message, details}
 * (client.ts кладе ЦІЛЕ тіло в e.details → вкладені дані на e.details.details).
 */

// ── types ────────────────────────────────────────────────────────────────────

export interface AnnexRequisitesSnapshot {
  legalNameFull: string | null;
  legalAddress: string | null;
  signerName: string | null;
  signerPosition: string | null;
  signerNameGenitive: string | null;
  signerBasis: string | null;
  iban: string | null;
  bankName: string | null;
  vatIpn: string | null;
  phone: string | null;
}

export interface AnnexContextResponse {
  contract: {
    id: string;
    name1c: string;
    number: string;
    signedDate: string | null; // ISO YYYY-MM-DD
  };
  account: {
    id: string;
    legalName: string;
    code: string;
    legalIdentityType: string;
    needsLegalIdentityConfirm: boolean;
    registryStatus: string | null;
    registryStatusVerifiedAt: string | null;
    requisitesFetchedAt: string | null;
    requisites: AnnexRequisitesSnapshot;
  };
  existingAnnexNumbers: string[];
}

export type AnnexGateMode =
  | "ok"
  | "ok_stored"
  | "manual"
  | "blocked"
  | "pending_confirmation"
  | "code_required";

export interface AnnexLookupResponse {
  state: string;
  gate: {
    mode: AnnexGateMode;
    blocked: boolean;
    blockReason: string | null;
  };
  anchor?: { code: string | null; source: "account" | "override" | null };
  stored?: { status: string | null; verifiedAt: string | null };
  fromCache?: boolean | null;
  fetchedAt?: string | null;
  recoversAt?: string | null;
  registry?: {
    type: string | null;
    fullName: string | null;
    shortName: string | null;
    location: string | null;
    ceoName: string | null;
    vatCode: string | null;
    statusCanonical: string | null;
    fetchedAt: string | null;
    fromCache: boolean | null;
  };
  nameGuard?: {
    checked: boolean;
    match: boolean | null;
    registryName: string | null;
    crmName: string | null;
    confirmed: boolean;
  };
  writeback?: { applied: string[] };
}

export interface AnnexDocPayload {
  clientRequestId?: string;
  confirmDuplicateAnnexNumber?: boolean;
  annexNumber: string;
  annexDate: string; // ДД.ММ.РРРР
  contractNumber: string;
  contractDate: string; // ДД.ММ.РРРР
  buyerType: "company" | "fop" | "unsure" | "";
  buyerFullName: string;
  buyerShortName: string;
  buyerAddress: string;
  signerName: string;
  signerPosition: string;
  signerNameGenitive: string;
  signerBasis: string;
  poaNumber: string;
  poaDate: string;
  iban: string;
  bankName: string;
  vatIpn: string;
  phone: string;
  edrpou: string;
  edoEmail: string;
}

export interface AnnexPreviewResponse {
  lines: Record<string, string>;
  missing: string[];
  duplicate: { exists: boolean; attachmentId: string | null; fileName: string | null };
}

export interface AnnexGenerateResponse {
  attachmentId: string;
  fileId: string;
  fileName: string;
  fileSha256: string;
  annexNumber: string;
  replay: boolean;
}

// ── calls ────────────────────────────────────────────────────────────────────

export function fetchAnnexContext(contractId: string): Promise<AnnexContextResponse> {
  return apiFetch<AnnexContextResponse>(
    `/Custom/contracts/${encodeURIComponent(contractId)}/annex/context`,
  );
}

export function annexRegistryLookup(
  contractId: string,
  body: { forceFresh?: boolean; confirmNameMismatch?: boolean; codeOverride?: string },
): Promise<AnnexLookupResponse> {
  return apiFetch<AnnexLookupResponse>(
    `/Custom/contracts/${encodeURIComponent(contractId)}/annex/registry-lookup`,
    { method: "POST", body },
  );
}

/** Write-gate §4.4 — тихий writeback ручних правок (рішення Igor: без чекбокса). */
export function annexRequisitesUpdate(
  contractId: string,
  fields: Record<string, string | null>,
): Promise<{ ok: boolean; applied: string[] }> {
  return apiFetch(`/Custom/contracts/${encodeURIComponent(contractId)}/annex/requisites`, {
    method: "POST",
    body: { fields },
  });
}

export function annexPreview(
  contractId: string,
  body: AnnexDocPayload,
): Promise<AnnexPreviewResponse> {
  return apiFetch<AnnexPreviewResponse>(
    `/Custom/contracts/${encodeURIComponent(contractId)}/annex/preview`,
    { method: "POST", body },
  );
}

/**
 * PDF-превʼю кроку 4 (фікс Igor): сервер рендерить tmp-.docx тією самою збіркою
 * і конвертує в PDF (без Attachment/Event/БД). 503 pdf_preview_unavailable
 * (немає soffice до image r2) → викликач падає назад на серверні рядки.
 */
export function annexPreviewPdf(
  contractId: string,
  body: AnnexDocPayload,
  signal?: AbortSignal,
): Promise<Blob> {
  return apiFetchBlob(
    `/Custom/contracts/${encodeURIComponent(contractId)}/annex/preview-pdf`,
    { method: "POST", body, signal },
  );
}

export function annexGenerate(
  contractId: string,
  body: AnnexDocPayload,
): Promise<AnnexGenerateResponse> {
  return apiFetch<AnnexGenerateResponse>(
    `/Custom/contracts/${encodeURIComponent(contractId)}/annex/generate`,
    { method: "POST", body },
  );
}

/** Вкладені details flat-тунелю: e.details = ціле тіло → корисне на .details. */
export function annexErrorDetails(e: unknown): Record<string, unknown> {
  if (e instanceof ApiClientError && e.details) {
    const inner = (e.details as { details?: unknown }).details;
    if (inner && typeof inner === "object") return inner as Record<string, unknown>;
    return e.details;
  }
  return {};
}

export function annexErrorCode(e: unknown): string | null {
  return e instanceof ApiClientError ? e.code : null;
}
